import { useEffect, useCallback, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import ReportFilters from "./components/ReportFilters"
import { PdfDataProvider } from "./contexts/PdfDataContext"

import { useBuilderReportTemplate } from "@/app/export/pdf/hooks/useBuilderReportTemplate"
import { buildDefaultItemsFromReportTemplate } from "@/app/export/pdf/utils/defaultLayout"
import { applyPdfTemplateContent } from "@/app/report/pdf/builder/utils/applyTemplateContent"
import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import {
  buildItemsFromPalette,
  isPaletteDragEvent,
} from "@/app/report/pdf/builder/utils/paletteItems"
import type { PdfTemplateContent } from "@/types/pdfTemplate"

import ChartPalette from "@/app/report/pdf/builder/components/ChartPalette"
import ReportCanvas from "@/app/report/pdf/builder/components/ReportCanvas"
import PropertyPanel from "@/app/report/pdf/builder/components/PropertyPanel"

import { RiSideBarLine } from "@remixicon/react"
import EditorToolbar from "./components/EditorToolbar"
import LoadTemplateDialog from "./components/LoadTemplateDialog"

import useRouter from "@/hooks/router/useRoute"
import { useBlockBrowserBack } from "@/hooks/router/useBlockBrowserBack"
import {
  useCreatePdfTemplateMutation,
  useUpdatePdfTemplateMutation,
  useUpdatePdfTemplateLastUsedMutation,
} from "@/hooks/api/usePdfTemplateQuery"
import { readPdfTemplateDetail, updatePdfTemplateLastUsed } from "@/app/api/pdfTemplate"
import { createGlobalStore } from "@/stores/global"

import "./pdfEditor.scss"
import "@/app/report/pdf/builder/reportBuilder.scss"

const ZOOM_STEPS = [50, 75, 100, 125, 150, 200]
// 양쪽 패널 폭 — 기본·범위, 브라우저 저장 키 (2026-10-01 요청)
const LEFT_PANEL_DEFAULT_PX = 250
const RIGHT_PANEL_DEFAULT_PX = 280
// 왼쪽 최소 250px — 팔레트 라벨이 한 줄에 들어가는 폭 (2026-10-01 결정)
const PANEL_WIDTH_RANGE = { left: [250, 480], right: [240, 600] } as const
const clampPanelWidth = (side: "left" | "right", width: number) =>
  Math.round(Math.min(PANEL_WIDTH_RANGE[side][1], Math.max(PANEL_WIDTH_RANGE[side][0], width)))
const readPanelWidth = (side: "left" | "right", fallback: number) => {
  try {
    const stored = Number(localStorage.getItem(`pdfEditor.${side}PanelWidth`))

    return stored > 0 ? clampPanelWidth(side, stored) : fallback
  } catch {
    return fallback
  }
}
const storePanelWidth = (side: "left" | "right", width: number) => {
  try {
    localStorage.setItem(`pdfEditor.${side}PanelWidth`, String(width))
  } catch {
    // 저장이 막힌 브라우저 — 이번 세션만 쓴다
  }
}

const DEFAULT_ZOOM = 100

export default function PdfEditorPage() {
  const { t } = useTranslation()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { setSnackBar } = createGlobalStore()

  // Only subscribe to what this component actually needs for rendering
  const currentPageId = usePdfBuilderStore((s) => s.currentPageId)
  const orientation = usePdfBuilderStore(
    (s) => s.pages.find((p) => p.id === s.currentPageId)?.config.orientation ?? "portrait",
  )

  const [isPaletteVisible, setIsPaletteVisible] = useState(true)
  // 양쪽 패널 — 숨기기와 폭 조절 (2026-10-01 요청). 폭은 이 브라우저에만 기억한다
  const [isPropertiesVisible, setIsPropertiesVisible] = useState(true)
  const [leftWidth, setLeftWidth] = useState(() => readPanelWidth("left", LEFT_PANEL_DEFAULT_PX))
  const [rightWidth, setRightWidth] = useState(() =>
    readPanelWidth("right", RIGHT_PANEL_DEFAULT_PX),
  )
  const [resizing, setResizing] = useState<"left" | "right" | null>(null)
  const [pdfTitle, setPdfTitle] = useState("")
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const [loadDialogOpen, setLoadDialogOpen] = useState(false)
  const [currentTemplateId, setCurrentTemplateId] = useState<number | null>(null)
  const [currentTemplateName, setCurrentTemplateName] = useState<string>("")
  const [isSaving, setIsSaving] = useState(false)

  // 저장한 뒤로 배치가 바뀌었는지. 편집 내용은 서버에만 남으므로 저장 전에 나가면 그대로 사라진다.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  useEffect(
    () =>
      usePdfBuilderStore.subscribe((state, prev) => {
        if (state.pages !== prev.pages) {
          setHasUnsavedChanges(true)
        }
      }),
    [],
  )

  // 브라우저 뒤로가기와 다른 페이지로의 이동을 막는다.
  // 문구는 다른 편집 화면(존 설정, 리포트 설정)이 쓰는 messages.confirmLeavePage 를 그대로 쓴다.
  useBlockBrowserBack(hasUnsavedChanges)

  // 새로고침·탭 닫기는 위 훅이 잡지 못하므로 따로 막는다
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }
    const handleBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [hasUnsavedChanges])

  const createMutation = useCreatePdfTemplateMutation()
  const updateMutation = useUpdatePdfTemplateMutation()
  const lastUsedMutation = useUpdatePdfTemplateLastUsedMutation()

  // 저장된 PDF 배치는 리포트 종류별로 따로 (2026-10-02 요청)
  const reportKind = usePdfBuilderStore((s) => s.filterReportKind)
  // 기본 구성을 이미 깔았거나 사용자가 빈 화면을 고른 리포트 — 다시 깔지 않는다
  const startedKinds = useRef(new Set<string>())
  // 리포트 템플릿 — 저장된 PDF 템플릿이 없을 때·새 템플릿을 만들 때 리포트와 같은 구성으로 시작하기 위한 출처 (#75)
  const { reportTemplate } = useBuilderReportTemplate()
  const seedFromReportTemplate = useCallback(() => {
    if (!reportTemplate) {
      return false
    }
    const store = usePdfBuilderStore.getState()
    const pageId = store.currentPageId ?? store.pages[0]?.id
    if (!pageId) {
      return false
    }
    buildDefaultItemsFromReportTemplate(reportTemplate, reportKind).forEach((item) =>
      usePdfBuilderStore.getState().addItem(pageId, item),
    )
    usePdfBuilderStore.getState().selectItem(null)

    return true
  }, [reportTemplate, reportKind])

  // Load and apply a template by id
  const loadAndApplyTemplate = useCallback(
    async (idfTemplate: number) => {
      try {
        const result = await queryClient.fetchQuery({
          queryKey: ["readPdfTemplateDetail", idfTemplate],
          queryFn: () => readPdfTemplateDetail(idfTemplate),
        })
        const detail = (result as any)?.response ?? result
        if (!detail?.content) return

        const templateContent: PdfTemplateContent = JSON.parse(detail.content)
        // 여러 페이지로 저장된 옛 템플릿은 한 페이지에 이어 붙인다 (Quick Export 와 같은 함수)
        applyPdfTemplateContent(templateContent)

        setCurrentTemplateId(idfTemplate)
        setCurrentTemplateName(detail.name ?? "")
        setPdfTitle(detail.name ?? "")
        // 방금 불러온 그대로이므로 바뀐 것이 없다
        setHasUnsavedChanges(false)

        // Update lastUsed
        await updatePdfTemplateLastUsed(idfTemplate).catch(() => {})
      } catch {
        // silently fail - user can load manually
      }
    },
    [queryClient],
  )

  // 편집 중인 PDF 템플릿(이름·id)도 리포트 종류마다 따로 들고 있는다 — 액티비티에서 불러온 템플릿을 그대로
  // 둔 채 트렌드에서 저장하면 액티비티 템플릿을 덮어쓴다 (2026-10-02 확인)
  const templateByKind = useRef<Record<string, { id: number | null; name: string; title: string }>>(
    {},
  )
  const lastKindRef = useRef(reportKind)
  useEffect(() => {
    if (lastKindRef.current === reportKind) {
      return
    }
    templateByKind.current[lastKindRef.current] = {
      id: currentTemplateId,
      name: currentTemplateName,
      title: pdfTitle,
    }
    const next = templateByKind.current[reportKind] ?? { id: null, name: "", title: "" }
    lastKindRef.current = reportKind
    setCurrentTemplateId(next.id)
    setCurrentTemplateName(next.name)
    setPdfTitle(next.title)
  }, [reportKind, currentTemplateId, currentTemplateName, pdfTitle])

  // 아무 작업도 없는 리포트로 들어오면 늘 Default(리포트 템플릿 구성)로 시작한다 — 마지막에 쓴 PDF 배치를
  // 자동으로 불러오지 않는다. 빈 화면이 필요하면 Templates > New (2026-10-02 요청).
  // 리포트 종류를 바꿀 때도 그 리포트를 처음 여는 것이면 같은 규칙으로 깐다
  useEffect(() => {
    if (startedKinds.current.has(reportKind)) {
      return
    }
    // 리포트 템플릿이 아직 안 왔으면 다음 렌더에서 다시 시도한다
    if (seedFromReportTemplate()) {
      startedKinds.current.add(reportKind)
      setHasUnsavedChanges(false)
    }
  }, [reportKind, seedFromReportTemplate])

  // Set initial current page
  useEffect(() => {
    if (currentPageId) return
    const { pages, setCurrentPage } = usePdfBuilderStore.getState()
    if (pages.length > 0) {
      setCurrentPage(pages[0].id)
    }
  }, [currentPageId])

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => {
      const nextStep = ZOOM_STEPS.find((s) => s > prev)
      return nextStep ?? prev
    })
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => {
      const prevSteps = ZOOM_STEPS.filter((s) => s < prev)
      return prevSteps.length > 0 ? prevSteps[prevSteps.length - 1] : prev
    })
  }, [])

  const handleZoomReset = useCallback(() => {
    setZoom(DEFAULT_ZOOM)
  }, [])

  const handleOrientationChange = useCallback((newOrientation: "portrait" | "landscape") => {
    const { currentPageId, updatePageConfig } = usePdfBuilderStore.getState()
    if (currentPageId) {
      updatePageConfig(currentPageId, { orientation: newOrientation })
    }
  }, [])

  // 캔버스(ReportCanvas) 밖, 가운데 영역 어디에 놓아도 맨 아래에 추가한다 — 캔버스 위에 놓은 것은
  // ReportCanvas 가 처리하고 preventDefault 해 두므로 여기서는 건너뛴다
  const handleWrapperDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!isPaletteDragEvent(e.dataTransfer)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
  }, [])
  const handleWrapperDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!isPaletteDragEvent(e.dataTransfer) || e.defaultPrevented) return
    e.preventDefault()
    const store = usePdfBuilderStore.getState()
    const paletteItem = store.draggedPaletteItem
    if (!paletteItem || !store.currentPageId) return
    const pageId = store.currentPageId
    buildItemsFromPalette(paletteItem, 0, Infinity).forEach((newItem) =>
      store.addItem(pageId, newItem),
    )
    store.setDraggingFromPalette(false)
  }, [])

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      usePdfBuilderStore.getState().selectItem(null)
    }
  }, [])

  const handleTogglePalette = useCallback(() => {
    setIsPaletteVisible((prev) => !prev)
  }, [])
  const handleToggleProperties = useCallback(() => {
    setIsPropertiesVisible((prev) => !prev)
  }, [])

  // 패널 가장자리를 끌어 폭을 바꾼다 — 포인터를 잡아 두고(setPointerCapture) 놓을 때 폭을 기억한다
  const startResize = useCallback(
    (side: "left" | "right") => (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      const target = event.currentTarget
      const startX = event.clientX
      const startWidth = side === "left" ? leftWidth : rightWidth
      setResizing(side)
      const onMove = (move: PointerEvent) => {
        const delta = move.clientX - startX
        const next = clampPanelWidth(
          side,
          side === "left" ? startWidth + delta : startWidth - delta,
        )
        if (side === "left") {
          setLeftWidth(next)
        } else {
          setRightWidth(next)
        }
      }
      const onUp = (up: PointerEvent) => {
        target.removeEventListener("pointermove", onMove)
        target.removeEventListener("pointerup", onUp)
        target.removeEventListener("pointercancel", onUp)
        setResizing(null)
        const delta = up.clientX - startX
        storePanelWidth(
          side,
          clampPanelWidth(side, side === "left" ? startWidth + delta : startWidth - delta),
        )
      }
      target.addEventListener("pointermove", onMove)
      target.addEventListener("pointerup", onUp)
      target.addEventListener("pointercancel", onUp)
      try {
        target.setPointerCapture(event.pointerId)
      } catch {
        // 포인터를 잡지 못해도 손잡이 위에서 끄는 동안은 동작한다
      }
    },
    [leftWidth, rightWidth],
  )

  const buildContent = useCallback(() => {
    const { pages } = usePdfBuilderStore.getState()
    const firstPage = pages[0]
    const orientation = firstPage?.config.orientation ?? "portrait"
    const size = firstPage?.config.size ?? "a4"

    const templateContent: PdfTemplateContent = {
      paperConfig: { size, orientation },
      pages,
    }
    return JSON.stringify(templateContent)
  }, [])

  // 이름 없이 Save 를 누르면 아무 반응이 없어 보이던 문제 — 이름 칸을 빨갛게 표시한다 (2026-09-28 요청)
  const [titleMissing, setTitleMissing] = useState(false)
  const handleSaveTemplate = useCallback(async () => {
    const name = pdfTitle.trim()
    if (!name) {
      setTitleMissing(true)

      return
    }
    setTitleMissing(false)

    setIsSaving(true)
    const content = buildContent()

    try {
      if (currentTemplateId) {
        // Update existing
        await updateMutation.mutateAsync({
          idfTemplate: currentTemplateId,
          name,
          reportType: reportKind,
          content,
        })
        await lastUsedMutation.mutateAsync(currentTemplateId)
      } else {
        // Create new
        const result = await createMutation.mutateAsync({
          name,
          reportType: reportKind,
          content,
        })
        const savedId = (result as any)?.response?.idfTemplate ?? (result as any)?.idfTemplate
        if (savedId) {
          setCurrentTemplateId(savedId)
          await lastUsedMutation.mutateAsync(savedId)
        }
      }
      setCurrentTemplateName(name)
      setHasUnsavedChanges(false)
      setSnackBar({
        open: true,
        message: t("messages.successSave", "Successfully saved"),
        severity: "success",
      })
    } catch {
      setSnackBar({
        open: true,
        message: t("messages.failSave", "Failed to save"),
        severity: "error",
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    pdfTitle,
    currentTemplateId,
    buildContent,
    updateMutation,
    createMutation,
    lastUsedMutation,
    setSnackBar,
    t,
  ])

  // Default(내장) 템플릿 = 설정한 리포트 템플릿 구성 그대로. 새 템플릿과 Load Template 의 Default 가 같은 동작이다 (#75)
  const applyDefaultTemplate = useCallback(() => {
    usePdfBuilderStore.getState().reset({ keepFilters: true })
    startedKinds.current.add(usePdfBuilderStore.getState().filterReportKind)
    seedFromReportTemplate()
    setCurrentTemplateId(null)
    setCurrentTemplateName("")
    setPdfTitle("")
    setHasUnsavedChanges(false)
  }, [seedFromReportTemplate])

  // New = 빈 화면. 기본 구성으로 시작하는 것은 들어올 때 자동이거나 Load Template 의 Default 다 (2026-10-02 요청)
  const handleNewTemplate = useCallback(() => {
    if (hasUnsavedChanges && !confirm(t("messages.confirmLeavePage"))) {
      return
    }
    usePdfBuilderStore.getState().reset({ keepFilters: true })
    startedKinds.current.add(usePdfBuilderStore.getState().filterReportKind)
    setCurrentTemplateId(null)
    setCurrentTemplateName("")
    setPdfTitle("")
    setHasUnsavedChanges(false)
  }, [hasUnsavedChanges, t])

  // 편집 내용은 화면을 벗어날 때 지운다.
  // 나가기 전에 지우면 위 훅의 확인 창에서 "취소" 를 눌러도 이미 사라진 뒤가 된다.
  useEffect(() => () => usePdfBuilderStore.getState().reset(), [])

  const handleClose = useCallback(() => {
    startedKinds.current.clear()
    router.push("/home")
  }, [router])

  return (
    <PdfDataProvider>
      <>
        <div className="PdfEditor">
          {/* Top Toolbar */}
          <EditorToolbar
            zoom={zoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            orientation={orientation}
            onOrientationChange={handleOrientationChange}
            onNewTemplate={handleNewTemplate}
            onLoadTemplate={() => setLoadDialogOpen(true)}
            onSaveTemplate={handleSaveTemplate}
            isSaving={isSaving}
            onClose={handleClose}
          />

          {/* 3-Column Content */}
          <div className="PdfEditor__content">
            {/* Left Panel - Components Palette */}
            {/* 숨긴 패널은 가장자리 띠로 남겨 다시 연다 (2026-10-01 요청) */}
            {!isPaletteVisible && (
              <button
                className="PdfEditor__panel-strip PdfEditor__panel-strip--left"
                onClick={handleTogglePalette}
                title={t("showPanel", "Show panel")}>
                <RiSideBarLine size={18} />
              </button>
            )}
            <div
              className={`PdfEditor__left-panel ${!isPaletteVisible ? "PdfEditor__left-panel--hidden" : ""}`}
              style={{ width: leftWidth, minWidth: leftWidth }}>
              <ChartPalette onHide={handleTogglePalette} />
            </div>
            {isPaletteVisible && (
              <div
                className={`PdfEditor__resizer${resizing === "left" ? " PdfEditor__resizer--active" : ""}`}
                onPointerDown={startResize("left")}
                role="separator"
                aria-orientation="vertical"
              />
            )}

            {/* Center Panel - Canvas */}
            <div className="PdfEditor__center-panel">
              <div className="ReportBuilder__canvas-container">
                <div
                  className="ReportBuilder__canvas-wrapper"
                  onClick={handleCanvasClick}
                  onDragOver={handleWrapperDragOver}
                  onDrop={handleWrapperDrop}>
                  <div
                    className="PdfEditor__canvas-zoom"
                    style={{ transform: `scale(${zoom / 100})` }}>
                    {/* 격자 라이브러리에 같은 배율을 알려야 확대 상태에서 클릭·드래그 좌표가 맞는다 */}
                    {currentPageId && <ReportCanvas scale={zoom / 100} />}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Title, Filters, Properties */}
            {isPropertiesVisible && (
              <div
                className={`PdfEditor__resizer${resizing === "right" ? " PdfEditor__resizer--active" : ""}`}
                onPointerDown={startResize("right")}
                role="separator"
                aria-orientation="vertical"
              />
            )}
            {!isPropertiesVisible && (
              <button
                className="PdfEditor__panel-strip PdfEditor__panel-strip--right"
                onClick={handleToggleProperties}
                title={t("showPanel", "Show panel")}>
                <RiSideBarLine size={18} style={{ transform: "scaleX(-1)" }} />
              </button>
            )}
            <div
              className={`PdfEditor__right-panel${!isPropertiesVisible ? " PdfEditor__right-panel--hidden" : ""}`}
              style={{ width: rightWidth, minWidth: rightWidth }}>
              {/* 패널 머리말 — 이름과 숨기기 (2026-10-01 요청) */}
              <div className="PdfEditor__right-header">
                <h3>{t("reportSettings", "Report Settings")}</h3>
                <button
                  className="PdfEditor__right-hide-btn"
                  onClick={handleToggleProperties}
                  title={t("hidePanel", "Hide panel")}>
                  <RiSideBarLine size={18} style={{ transform: "scaleX(-1)" }} />
                </button>
              </div>
              {/* PDF Title */}
              <div className="PdfEditor__right-section">
                <label className="PdfEditor__section-label">{t("pdfTitle", "Report Name")}</label>
                <input
                  className={`PdfEditor__title-input${titleMissing ? " PdfEditor__title-input--error" : ""}`}
                  type="text"
                  value={pdfTitle}
                  onChange={(e) => {
                    setPdfTitle(e.target.value)
                    if (e.target.value.trim()) {
                      setTitleMissing(false)
                    }
                  }}
                  placeholder={t("enterTitle", "Enter title...")}
                />
                {titleMissing && (
                  <span className="PdfEditor__title-error">
                    {t("reportNameRequired", "Enter a report name to save")}
                  </span>
                )}
              </div>

              {/* Report Filters */}
              <ReportFilters />

              {/* Property Panel */}
              <PropertyPanel />
            </div>
          </div>
        </div>
      </>

      <LoadTemplateDialog
        open={loadDialogOpen}
        onClose={() => setLoadDialogOpen(false)}
        currentTemplateId={currentTemplateId}
        onApply={(id) => {
          if (hasUnsavedChanges && !confirm(t("messages.confirmLeavePage"))) {
            return
          }
          loadAndApplyTemplate(id)
          setLoadDialogOpen(false)
        }}
        onApplyDefault={() => {
          if (hasUnsavedChanges && !confirm(t("messages.confirmLeavePage"))) {
            return
          }
          // Default = 설정한 리포트 템플릿 구성 그대로 (저장된 PDF 템플릿이 아니라 내장 배치)
          applyDefaultTemplate()
          setLoadDialogOpen(false)
        }}
        onDeleted={(id) => {
          if (currentTemplateId === id) {
            setCurrentTemplateId(null)
            setCurrentTemplateName("")
          }
        }}
      />
    </PdfDataProvider>
  )
}
